<script setup lang="ts">
import { useHead } from '@vueuse/head';
import { onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';

import Gallery from '@/components/about-us/Gallery.vue';
import NavSections, { Section } from '@/components/about-us/NavSections.vue';
import OurFamily from '@/components/about-us/our-family/OurFamily.vue';
import OurCommunity from '@/components/about-us/OurCommunity.vue';
import OurEnvironment from '@/components/about-us/OurEnvironment.vue';
import OurGuarantee from '@/components/about-us/OurGuarantee.vue';
import OurMission from '@/components/about-us/OurMission.vue';
import OurStory from '@/components/about-us/OurStory.vue';
import Container from '@/components/base/layout/Container.vue';
import BaseBodyText from '@/components/base/typography/BaseBodyText.vue';
import Header1 from '@/components/base/typography/Header1.vue';

const heroImage = nutshell['static_img/about-us/group-photo-7a8329ad.jpg'];

const route = useRoute();
const section = ref<Section>('our-story');

useHead({
  meta: [
    {
      content: `Your source for the best nuts, snacks, dried fruit & pantry staples at home, in the office or on-the-go!"`,
      key: 'og:description',
      property: 'og:description',
    },
    { property: 'og:image', content: heroImage, key: 'og:image' },
    { property: 'og:title', content: 'About Us | Nuts.com', key: 'og:title' },
    { property: 'og:url', content: 'https://nuts.com/about-us', key: 'og:url' },
  ],
  title: 'About Us | Nuts.com',
});

onMounted(() => {
  if (route.hash) {
    const possibleSections: Section[] = [
      'our-story',
      'our-environment',
      'our-community',
      'our-mission',
      'our-guarantee',
      'our-family',
    ];
    const matchedSection = possibleSections.find((sec) => route.hash.includes(sec));
    if (matchedSection) section.value = matchedSection;
  }
});
</script>

<template>
  <Container class="py-6">
    <div class="grid grid-cols-12 gap-7">
      <div class="col-span-12 md:col-span-4">
        <Gallery />
      </div>
      <div class="col-span-12 md:col-span-8">
        <Header1>About Us</Header1>
        <BaseBodyText class="mt-5">
          In 1929, on the brink of the Depression, our father and grandfather “Poppy” Sol took a
          bold step. 22 years old, he had already worked fulltime for 10 years to support his
          family. Yet he was ambitious, and wanted to live the American Dream and make the lives of
          everyone around him better. So, donning his best of suits and an assured smile on his
          face, Sol walked away from the bank with a loan to start his own business, the Newark Nut
          Company. The rest is history. <strong>Let us tell you all about it:</strong>
        </BaseBodyText>
        <NavSections v-model="section" class="mt-4" />
        <div class="mt-5">
          <OurStory v-show="section === 'our-story'" id="our-story" />
          <OurFamily v-show="section === 'our-family'" id="our-family" />
          <OurEnvironment v-show="section === 'our-environment'" id="our-environment" />
          <OurCommunity v-show="section === 'our-community'" id="our-community" />
          <OurMission v-show="section === 'our-mission'" id="our-mission" />
          <OurGuarantee v-show="section === 'our-guarantee'" id="our-guarantee" />
        </div>
      </div>
    </div>
  </Container>
</template>
